import dayjs from "dayjs";

const createBatteryOption = (val, dataInput = [],yAxisLabels = []) => {
  const isEmpty = !Array.isArray(dataInput) || dataInput.length === 0;
  
  const parsedMin = parseFloat(val?.min);
  const parsedMax = parseFloat(val?.max);
  const isValidMin = isFinite(parsedMin);
  const isValidMax = isFinite(parsedMax);
  const rawMin = isValidMin ? parsedMin : 0;
  const rawMax = isValidMax ? parsedMax : 100;
  const attrMin = Math.min(rawMin, rawMax);
  const attrMax = Math.max(rawMin, rawMax);
  
  const baseOption = {
    tooltip: {
      trigger: "item",
      borderColor: "#6F7171",
      formatter: function (params) {
        const date = new Date(params.data?.[0]);
        const formattedDate = dayjs(date).format("MM-DD-YYYY HH:mm:ss");
        return `${formattedDate}<br />Battery (%) ${params.data?.[1] ?? "-"}`;
      },
    },
    grid: {
      left: 60,
      top: 20,
      right: 20,
      bottom: 60,
      backgroundColor: "#FFFFFF",
    },
    xAxis: {
      type: "time",
      name: "Time",
      nameGap: 40,
      nameLocation: "middle",
      axisLabel: {
        formatter: (value) => dayjs(value).format("MM/DD/YYYY\nh:mm A"),
      },
    },
    yAxis: {
      type: "value",
      name: "Battery (%)",
      nameGap: 40,
      nameRotate: 90,
      nameLocation: "middle",
      splitLine: {
        show: true,
        lineStyle: {
          color: "#d4d4d4",
          width: 1.5,
          type: "solid",
        },
      },
    },
    series: [
      {
        name: "Battery (%)",
        data: [],
        type: "line",
        lineStyle: { width: 2 },
        symbolSize: 8,
        itemStyle: {
          color: "#00B0F0",
          borderWidth: 1,
          borderColor: "#333",
        },
        emphasis: {
          itemStyle: {
            borderWidth: 2,
            borderColor: "#000",
            symbolSize: 10,
          },
        },
      },
    ],
  };

  if (isEmpty) {
    return {
      ...baseOption,
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: "No data available",
          fontSize: 18,
          fill: "#888",
          fontWeight: 500,
        },
      },
    };
  }

  return {
    dataZoom: [
      {
        type: "slider",
        start: 0,
        end: 100,
        show: true,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "item",
      borderColor: "#6F7171",
      formatter: function (params) {
        if (params.componentType === "markLine") {
          if (params.value === attrMin) {
            return `Min Alert limit: ${params.value}`;
          } else if (params.value === attrMax) {
            return `Max Alert limit: ${params.value}`;
          }
          return `Alert limit: ${params.value}`;
        }
        const date = new Date(params.data[0]);
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date
          .getDate()
          .toString()
          .padStart(2, "0")}-${date.getFullYear()} ${date
          .getHours()
          .toString()
          .padStart(2, "0")}:${date
          .getMinutes()
          .toString()
          .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        return `${formattedDate}<br />Battery (%) ${params.data[1]}`;
      },
    },
    grid: {
      left: 60,
      top: 20,
      right: 20,
      bottom: 100,
    },
    xAxis: [
      {
        type: "time",
        name: "Time",
        boundaryGap: false,
        nameGap: 40,
        nameLocation: "middle",
        axisPointer: {
          label: {
            formatter: function (params) {
              return dayjs(params.value).format("MM-DD-YYYY HH:mm:ss");
            },
          },
          handle: {
            show: false,
          },
        },
        axisLabel: {
          formatter: function (value) {
            return dayjs(value).format("MM/DD/YYYY\nh:mm A");
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "Battery (%)",
        nameGap: 40,
        nameRotate: 90,
        nameLocation: "middle",
        splitLine: {
          show: true,
          lineStyle: {
            color: "#d4d4d4",
            width: 1.5,
            type: "solid",
          },
        },
        axisLabel: {
          formatter: function (value) {
            if (value === attrMin || value === attrMax) {
              return `{bg|${value}}`;
            }
            return value;
          },
          rich: {
            bg: {
              backgroundColor: "#FF8027",
              color: "#FFF",
              padding: [2, 4],
              borderRadius: 4,
            },
          },
        },
      },
    ],
    visualMap: {
      show: false,
      dimension: 1,
      pieces: [
        { gt: attrMin - 1, lte: attrMax, color: "#00B845" },
        { lte: attrMin >= attrMax ? attrMin-1 : attrMin, color: "#FF8027" },
        { gt: attrMax, color: "#FF8027" },
      ],
    },
    series: [
      {
        name: "Battery (%)",
        data: Array.isArray(dataInput) && dataInput?.length > 0 ? dataInput : [],
        type: "line",
        lineStyle: {
          width: 2,
        },
        symbolSize: 8,
        width: 4,
        itemStyle: {
          color: function (params) {
            const value = params.data[1];
            if (value >= attrMin && value <= attrMax) {
              return "#00B845";
            }
            return "#FF8027";
          },
          borderWidth: 1,
          borderColor: "#333",
        },
        emphasis: {
          itemStyle: {
            borderWidth: 2,
            borderColor: "#000",
            symbolSize: 10,
          },
        },
        markLine: {
          silent: false,
          symbol: ["none", "none"],
          lineStyle: {
            color: "orange",
            type: "dotted",
            width: 2,
            //type: [8, 6],
          },
          emphasis: {
            lineStyle: {
              width: 1,
            },
          },
          data: [
            {
              yAxis: attrMin,
              label: {
                show: true,
                formatter: attrMin,
                position: "start",
                distance: 5,
                color: "#FFF",
                backgroundColor: "#FF8027",
                padding: [2, 4],
                borderRadius: 4,
              },
            },
            {
              yAxis: attrMax,
              label: {
                show: true,
                formatter: attrMax,
                position: "start",
                distance: 5,
                color: "#FFF",
                backgroundColor: "#FF8027",
                padding: [2, 4],
                borderRadius: 4,
              },
            },
          ],
        },
        markArea: {
          silent: true,
          itemStyle: {
            color: "rgba(255, 235, 224, 0.6)",
            borderColor: "transparent",
          },
          data: [
            [
              {
                yAxis: Number(yAxisLabels[0]?.replace(/,/g, "")) || 0,
              },
              {
                yAxis: attrMin,
              },
            ],
            [
              {
                yAxis: attrMax,
              },
              {
                yAxis: Number(yAxisLabels[yAxisLabels.length - 1]?.replace(/,/g, "")) || 100,
              },
            ],
          ],
          z: -1,
        },
      },
    ],
  };
};

export default createBatteryOption;
